import { useState } from 'react'

import { DefaultMenuItem, MenuItemDef } from 'ag-grid-enterprise'
import { Trash } from 'lucide-react'
import { renderToString } from 'react-dom/server'

import { createColDefs } from '@fortem-monorepo/react-ag'
import { createContextMenu, defaultGetRowId } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { CustomAgGridProps } from '@fortem-monorepo/react-ag'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { useConfirmModal } from '@fortem-monorepo/react-spa'
import { useUserContext } from '@fortem-monorepo/react-spa'
import { JSONTable } from '@fortem-monorepo/react-ui'
import { Swapagg } from '@fortem-monorepo/web-sdk'

import { useDeleteMappingMutation } from '@/react-query/mutations/mappings'

export interface AllMappingsTableProps extends CustomAgGridProps {}

export const AllMappingsTable: React.FC<AllMappingsTableProps> = ({ query, rowData, ...props }) => {
  const { hasPermissions } = useUserContext()
  const [dialogData, setDialogData] = useState<Swapagg.Mapping | null>(null)

  const deleteMappingMutation = useDeleteMappingMutation()

  const { Dialog: DeleteModal, withConfirmation } = useConfirmModal({
    title: 'Delete Mapping',
  })

  const hasWritePermissions = hasPermissions((permissions) => ({
    permissions: [permissions.WRITE_MAPPINGS],
  }))

  const getContextMenuItems = createContextMenu<Swapagg.Mapping>((params) => [
    ...(hasWritePermissions
      ? ([
          {
            name: 'Delete Mapping',
            action: () => {
              setDialogData(params.node?.data ?? null)
              const id = params.node?.data?.id

              if (id !== undefined) {
                withConfirmation(
                  async () => await deleteMappingMutation.mutateAsync({ id: id.toString() }),
                )
              }
            },
            icon: renderToString(<Trash size={16} />),
            cssClasses: ['hover:bg-red-100/50!', 'font-medium!'],
          },
          'separator',
        ] satisfies (DefaultMenuItem | MenuItemDef<Swapagg.Mapping>)[])
      : ([] as never[])),
    'copy',
    'copyWithHeaders',
    'copyWithGroupHeaders',
    'separator',
    'export',
  ])

  const allMappingColDefs = createColDefs<Swapagg.Mapping>([
    { field: 'id', hide: true },
    { field: 'instrument_id', hide: true },
    { field: 'structurename' },
    { field: 'is_agent_id' },
    { field: 'external_id' },
    { field: 'provider' },
    { field: 'comment' },
    { field: 'from_date' },
  ])

  return (
    <>
      <Widget exposed className={'h-full'}>
        <WidgetHeaderWithQuery query={query} title="All Mappings" />
        <WidgetContent loading={query?.isLoading}>
          <CustomAgGrid
            domLayout={'normal'}
            columnDefs={allMappingColDefs}
            getContextMenuItems={getContextMenuItems}
            rowData={rowData}
            getRowId={defaultGetRowId}
            {...props}
          />
        </WidgetContent>
      </Widget>
      <DeleteModal>
        <div className={'my-4'}>
          <JSONTable data={[dialogData]} excludeKeys={['id', 'instrument_id', 'comment']} />
        </div>
      </DeleteModal>
    </>
  )
}
